/* File: QuestionRenderer.jsx
 * Path: ananunadm/src/components/Forms/QuestionRenderer.jsx
 *
 * Renders a single question's input for respondents (answer / preview / public
 * pages). Controlled: receives `value` and reports changes via `onChange`.
 * Pure presentation — never trusts client; the server re-validates everything.
 */
import React from "react";
import {
	TextInput, Textarea, NumberInput, Radio, Checkbox, Select, Rating, Group,
	Stack, Text, Divider, SegmentedControl, Title, Table, Box
} from "@mantine/core";
import { pickLocale, questionMeta } from "../../pages/Forms/formsShared";

export default function QuestionRenderer({ question: q, value, onChange, lang = "en", error }) {
	const meta = questionMeta(q.type);
	const label = pickLocale(q.label, lang);
	const help = pickLocale(q.description, lang) || pickLocale(q.helpText, lang);

	// layout-only blocks
	if (q.type === "sectionTitle") return <Title order={3} mt="md">{label}</Title>;
	if (q.type === "description") return <Text color="dimmed">{label || help}</Text>;
	if (q.type === "divider") return <Divider my="sm" />;

	const set = (v) => onChange(v);
	const opts = (q.options || []).map((o) => ({ value: o.value, label: pickLocale(o.label, lang) || o.value }));

	let control = null;
	switch (q.type) {
		case "longText":
			control = <Textarea minRows={3} value={value || ""} onChange={(e) => set(e.currentTarget.value)} placeholder={pickLocale(q.placeholder, lang)} />;
			break;
		case "number":
			control = <NumberInput value={value ?? ""} onChange={set} min={q.validation?.min ?? undefined} max={q.validation?.max ?? undefined} />;
			break;
		case "email":
			control = <TextInput type="email" value={value || ""} onChange={(e) => set(e.currentTarget.value)} placeholder="name@company.com" />;
			break;
		case "phone":
			control = <TextInput value={value || ""} onChange={(e) => set(e.currentTarget.value)} placeholder="+374 ..." />;
			break;
		case "url":
			control = <TextInput value={value || ""} onChange={(e) => set(e.currentTarget.value)} placeholder="https://" />;
			break;
		case "date":
			control = <TextInput type="date" value={value || ""} onChange={(e) => set(e.currentTarget.value)} />;
			break;
		case "time":
			control = <TextInput type="time" value={value || ""} onChange={(e) => set(e.currentTarget.value)} />;
			break;
		case "radio":
		case "yesNo":
		case "priority":
		case "agreement":
			control = (
				<Radio.Group value={value || ""} onChange={set}>
					<Stack spacing="xs" mt="xs">
						{opts.map((o) => <Radio key={o.value} value={o.value} label={o.label} />)}
					</Stack>
				</Radio.Group>
			);
			break;
		case "checkbox":
			control = (
				<Checkbox.Group value={value || []} onChange={set}>
					<Stack spacing="xs" mt="xs">
						{opts.map((o) => <Checkbox key={o.value} value={o.value} label={o.label} />)}
					</Stack>
				</Checkbox.Group>
			);
			break;
		case "dropdown":
			control = <Select data={opts} value={value || null} onChange={set} clearable searchable placeholder="—" />;
			break;
		case "consent":
			control = <Checkbox checked={value === true} onChange={(e) => set(e.currentTarget.checked)} label={label || "I agree"} />;
			break;
		case "rating":
			control = <Rating value={Number(value) || 0} onChange={set} count={q.scaleMax || 5} size="lg" />;
			break;
		case "linearScale":
		case "nps": {
			const min = q.scaleMin ?? (q.type === "nps" ? 0 : 1);
			const max = q.scaleMax ?? (q.type === "nps" ? 10 : 5);
			const range = [];
			for (let i = min; i <= max; i += 1) range.push(String(i));
			control = (
				<Stack spacing={4}>
					<SegmentedControl value={value != null ? String(value) : ""} onChange={(v) => set(Number(v))} data={range} fullWidth size="xs" />
					<Group position="apart">
						<Text size="xs" color="dimmed">{pickLocale(q.scaleMinLabel, lang)}</Text>
						<Text size="xs" color="dimmed">{pickLocale(q.scaleMaxLabel, lang)}</Text>
					</Group>
				</Stack>
			);
			break;
		}
		case "matrixRating": {
			const rows = q.rows || [];
			const mval = value && typeof value === "object" ? value : {};
			control = (
				<Table>
					<tbody>
						{rows.map((row) => (
							<tr key={row.value}>
								<td><Text size="sm">{pickLocale(row.label, lang) || row.value}</Text></td>
								<td>
									<Rating value={Number(mval[row.value]) || 0} count={q.scaleMax || 5} onChange={(v) => set({ ...mval, [row.value]: v })} />
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			);
			break;
		}
		case "employee":
		case "department":
			// Corporate selectors: if the host page passes resolved options via
			// q.__options it renders a Select, else a plain text fallback.
			control = q.__options
				? <Select data={q.__options} value={value || null} onChange={set} searchable clearable />
				: <TextInput value={value || ""} onChange={(e) => set(e.currentTarget.value)} placeholder={meta.label?.en} />;
			break;
		case "shortText":
		default:
			control = <TextInput value={value || ""} onChange={(e) => set(e.currentTarget.value)} placeholder={pickLocale(q.placeholder, lang)} />;
			break;
	}

	return (
		<Box>
			{q.type !== "consent" && (
				<Text weight={500} size="sm" mb={4}>
					{label} {q.required && <Text span color="red">*</Text>}
				</Text>
			)}
			{help && q.type !== "consent" && <Text size="xs" color="dimmed" mb={6}>{help}</Text>}
			{control}
			{error && <Text size="xs" color="red" mt={4}>{error}</Text>}
		</Box>
	);
}
